import { useEffect, useMemo, useRef, useSyncExternalStore } from "react";
import {
  type PublishedPageContext,
  clearAiChatPageContext,
  createPageContextOwner,
  publishAiChatPageContext,
  readAiChatPageContext,
  subscribeToAiChatPageContext,
} from "./pageContext";

/**
 * Publishes this page's context for as long as the page is mounted. Renders
 * nothing and holds no state, so it is safe to call from a page that re-renders
 * on every keystroke.
 *
 * Pass `null` while the page has nothing meaningful yet — a calendar that has
 * not resolved its location would otherwise announce a half-loaded view.
 */
export function useAiChatPageContext(published: PublishedPageContext | null) {
  const owner = useMemo(createPageContextOwner, []);
  const latest = useRef(published);
  latest.current = published;

  // Pages rebuild this object on every render, so the effect keys on the value
  // rather than the identity — otherwise every keystroke elsewhere on the page
  // would republish.
  const serialized = published === null ? null : JSON.stringify(published);

  useEffect(() => {
    const value = latest.current;
    if (value === null) {
      clearAiChatPageContext(owner);
      return;
    }
    publishAiChatPageContext(owner, value);
  }, [owner, serialized]);

  useEffect(() => () => clearAiChatPageContext(owner), [owner]);
}

export function useCurrentAiChatPageContext() {
  return useSyncExternalStore(
    subscribeToAiChatPageContext,
    readAiChatPageContext,
  );
}
